/*
 * Copyright (c) 2026, Amblique
 * All rights reserved.
 */

/**
 * Base Information Section
 * Presents breadcrumb and product description.
 * - Desktop: Used in the right column alongside other product info
 * - Mobile: Rendered above the gallery section
 */

import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Heading,
  Skeleton,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import Link from '@salesforce/retail-react-app/app/components/link';

import {Markup} from '@amblique/base/app/components/shared/markup';
import BazaarvoiceInlineRating from '@amblique/king-living/app/components/bazaarvoice/inline-rating';
import Breadcrumbs from '@amblique/king-living/app/components/breadcrumbs';
import {BAZAARVOICE_REVIEWS_SECTION_ID} from '@amblique/king-living/app/utils/bazaarvoice';
import {
  resolvePdpBaseInformationTitle,
  SIMPLE_PRODUCT_TYPE,
} from '@amblique/king-living/app/utils/product-utils';

const ProductDescription = ({description, styles}) => {
  if (!description) {
    return null;
  }

  return (
    <Box className="product-view__long-description" {...styles.baseInformationDescription}>
      <Markup source={description} />
    </Box>
  );
};

ProductDescription.propTypes = {
  description: PropTypes.oneOfType([PropTypes.string]),
  styles: PropTypes.object.isRequired,
};

const BaseInformationSection = ({
  category,
  hideChooseYourLayout = false,
  product,
  isSimpleProduct,
  ratingsSlot = null,
  simpleProductType,
}) => {
  const styles = useMultiStyleConfig('ProductDetail', {});
  const {title: productTitle, titleUsesMarkup} = resolvePdpBaseInformationTitle(product, {
    categoryTree: category,
    hideChooseYourLayout,
  });
  const shouldShowShortDescription = !hideChooseYourLayout &&
    isSimpleProduct &&
    simpleProductType === SIMPLE_PRODUCT_TYPE.COLOR_SIZE_OPTIONS &&
    product?.shortDescription;

  const defaultRatingsSlot = (
    <BazaarvoiceInlineRating
      product={product}
      redirectUrl={`#${BAZAARVOICE_REVIEWS_SECTION_ID}`}
    />
  );

  return (
    <VStack className="product-view__base-information" {...styles.baseInformationStack}>
      {category?.length > 0 && (
        <Breadcrumbs
          variant="productDetail"
          breadcrumbs={[
            {id: 'home', label: 'Home', href: '/'},
            ...category.map((cat) => ({
              id: cat.id,
              label: cat.name,
              href: `/category/${cat.id}`,
            })),
          ]}
        />
      )}

      <VStack className="product-view__base-information-package" {...styles.baseInformationProductPackage}>
        <VStack className="product-view__base-information-info" {...styles.baseInformationProductInfo}>
          {productTitle && (
            <Skeleton isLoaded={productTitle}>
              {titleUsesMarkup ? (
                <Box className="product-view__base-information-title" {...styles.baseInformationTitle}>
                  <Markup source={productTitle} />
                </Box>
              ) : (
                <Heading as="h1" className="product-view__base-information-title" {...styles.baseInformationTitle}>
                  {productTitle}
                </Heading>
              )}
            </Skeleton>
          )}

          {shouldShowShortDescription && (
            <Skeleton
              className="product-view__short-description-skeleton"
              isLoaded={product?.shortDescription}
              {...styles.baseInformationShortDescriptionSkeleton}
            >
              <Link
                className="product-view__short-description"
                to={`/category/${product.primaryCategoryId}`}
                {...styles.baseInformationShortDescription}
              >
                <Text
                  className="product-view__short-description-text"
                  {...styles.baseInformationShortDescriptionText}
                >
                  {product.shortDescription}
                </Text>
              </Link>
            </Skeleton>
          )}

          <Box className="product-view__base-information-ratings" {...styles.baseInformationRatings}>
            {ratingsSlot ?? defaultRatingsSlot}
          </Box>
        </VStack>

        {product?.longDescription && (
          <Skeleton
            className="product-view__long-description-skeleton"
            isLoaded={product?.longDescription}
          >
            <ProductDescription description={product.longDescription} styles={styles} />
          </Skeleton>
        )}
      </VStack>
    </VStack>
  );
};

BaseInformationSection.displayName = 'BaseInformationSection';
BaseInformationSection.propTypes = {
  category: PropTypes.array,
  hideChooseYourLayout: PropTypes.bool,
  isSimpleProduct: PropTypes.bool,
  product: PropTypes.object,
  ratingsSlot: PropTypes.node,
  simpleProductType: PropTypes.string,
};

export default BaseInformationSection;
